import {
  CEFR_LEVELS,
  EXAM_DURATION_MINUTES,
  EXAM_SECTION_COUNTS,
  EXAM_SECTIONS,
  type CefrLevel,
  type ExamSection,
} from "@rmnlng/shared";
import { ArrowLeft, Bookmark, BookmarkCheck, Check, Clock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useExamSubmit, type ExamQuestionEnvelope } from "@/lib/exam";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// Session state — per-question userAnswer + flagged. Index-keyed by
// `position - 1` so the array maps 1:1 to the envelope.
type AnswerState = { userAnswer: string; flagged: boolean };

// Run screen — `/levels/:level/exam/run`. State is per-mount: if the
// user navigates away mid-exam, their progress is lost (matches
// "abandoned attempts sit on the server until cooldown" semantics).
//
// Scaffold treatment of polish items per the plan: section-progress
// dots are the simple "N of 50" counter; timer is omitted; flag is in.
export function ExamSessionPage() {
  const { level } = useParams<{ level: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();

  const isValid = CEFR_LEVELS.includes(level as CefrLevel);
  const targetLevel = level as CefrLevel;

  // Questions arrive via location.state from the lobby. If a user lands
  // here directly (deep link / refresh), we have no questions — bounce
  // back to the lobby. The attempt row exists server-side, but we
  // didn't keep it in URL or local storage, so resuming isn't possible
  // in the scaffold. Acceptable trade-off; a follow-up could persist
  // the attempt in localStorage.
  const stateFromLobby = location.state as {
    attemptId?: string;
    startedAt?: string;
    questions?: ExamQuestionEnvelope[];
  } | null;
  const attemptId = stateFromLobby?.attemptId;
  const questions = stateFromLobby?.questions;
  const startedAt = stateFromLobby?.startedAt;

  const submit = useExamSubmit(targetLevel);

  // Local state — initial value derived once from the questions length,
  // even though the array shouldn't change after mount.
  const [answers, setAnswers] = useState<AnswerState[]>(() =>
    Array.from({ length: questions?.length ?? 0 }, () => ({ userAnswer: "", flagged: false })),
  );
  const [index, setIndex] = useState(0);

  // Soft timer — `startedAt` comes from the server's attempt row.
  // We tick a `now` cursor every second so the rendered countdown
  // updates without re-deriving on every keystroke. No auto-submit:
  // expiry is purely a visual nag (yellow at ≤2 min, red at ≤30 s,
  // 0:00 if past). Server doesn't enforce — late submissions still
  // grade. Per the Phase 7 design's open-question lean.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Bail-outs after hooks so the hook order is stable across renders.
  if (!isValid) return <Navigate to="/levels" replace />;
  if (!attemptId || !questions || questions.length === 0) {
    return <Navigate to={`/levels/${targetLevel}/exam`} replace />;
  }

  const total = questions.length;
  const isLast = index >= total - 1;
  const current = questions[index]!;
  const currentAnswer = answers[index]!;

  function setAnswer(updater: (prev: AnswerState) => AnswerState) {
    setAnswers((prev) => prev.map((a, i) => (i === index ? updater(a) : a)));
  }

  async function onSubmit() {
    if (!attemptId) return;
    try {
      const result = await submit.mutateAsync({
        attemptId,
        answers: answers.map((a, i) => ({
          position: i + 1,
          userAnswer: a.userAnswer,
          flagged: a.flagged,
        })),
      });
      navigate(`/levels/${targetLevel}/exam/result`, {
        state: { result },
        replace: true,
      });
    } catch {
      // Surface in line — not implemented at scaffold level beyond a
      // disabled-button toggle. Real failures are rare here (cooldown
      // doesn't apply to submit, only start).
    }
  }

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-3.5">
      <SessionHeader
        questions={questions}
        currentIndex={index}
        flagged={currentAnswer.flagged}
        startedAt={startedAt}
        now={now}
        onBack={() => {
          // Confirm before abandoning. Native window.confirm is fine
          // for the scaffold — a custom modal would match the design
          // hi-fi, but the safety net is what matters now.
          if (confirm(t("exam.abandon_confirm"))) {
            navigate(`/levels/${targetLevel}`);
          }
        }}
        onToggleFlag={() => setAnswer((a) => ({ ...a, flagged: !a.flagged }))}
      />

      {/* Question card */}
      <div className="flashcard flex-1 gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-text-3">
          {t(`exam.section_${current.section}`)}
        </div>
        <QuestionBody envelope={current} />

        {/* Answer widget — MCQ buttons or type-in input. */}
        <div className="mt-2">
          {(current.section === "vocab_mcq" || current.section === "grammar_mcq") && (
            <McqWidget
              options={current.question.options}
              selected={currentAnswer.userAnswer}
              onSelect={(option) => setAnswer((a) => ({ ...a, userAnswer: option }))}
            />
          )}
          {(current.section === "vocab_type" ||
            current.section === "grammar_fill" ||
            current.section === "grammar_transform") && (
            <Input
              autoFocus
              value={currentAnswer.userAnswer}
              onChange={(e) => setAnswer((a) => ({ ...a, userAnswer: e.target.value }))}
              placeholder={t("exam.type_placeholder")}
            />
          )}
        </div>
      </div>

      {/* Footer — Prev / Next or Submit on last question. */}
      <div className="grid grid-cols-2 gap-2.5">
        <Button variant="secondary" onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={index === 0}>
          {t("exam.prev")}
        </Button>
        {isLast ? (
          <Button onClick={onSubmit} disabled={submit.isPending}>
            <Check className="size-4" />
            {submit.isPending ? t("exam.submitting") : t("exam.submit")}
          </Button>
        ) : (
          <Button onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}>{t("exam.next")}</Button>
        )}
      </div>
    </div>
  );
}

// ─── Question body — branches by section ─────────────────────────────
function QuestionBody({ envelope }: { envelope: ExamQuestionEnvelope }) {
  if (envelope.section === "vocab_mcq" || envelope.section === "vocab_type") {
    return (
      <div className="text-center">
        <div className="text-[28px] font-semibold tracking-[-0.02em]">{envelope.question.lemma}</div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.08em] text-text-3">
          {envelope.question.language === "RO" ? "RO → RU" : "RU → RO"}
        </div>
      </div>
    );
  }
  // Grammar sections: render the sentence with a `___` blank + RU
  // translation underneath. Same shape across all 3 grammar sections;
  // transform additionally renders a "source" sub-card above.
  const showSource = envelope.section === "grammar_transform";
  return (
    <div className="flex flex-col gap-2">
      {showSource && envelope.section === "grammar_transform" && (
        <div className="rounded-[var(--r-sm)] bg-[color:var(--surface-2)] px-3 py-2 text-[13px] text-text-2">
          <div className="font-medium">{envelope.question.sourceSentence}</div>
          <div className="mt-0.5 text-[11px] text-text-3">{envelope.question.sourceTranslation}</div>
        </div>
      )}
      <div className="text-[18px] font-medium leading-snug">{envelope.question.sentence}</div>
      {envelope.question.translation && <div className="text-[12px] text-text-3">{envelope.question.translation}</div>}
      {envelope.question.hints.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {envelope.question.hints.map((h, i) => (
            <span
              key={i}
              className="rounded-full px-2 py-0.5 font-mono text-[10px]"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              {h}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Header chrome ───────────────────────────────────────────────────
//
// Five width-weighted section bars per design hi-fi. Each bar's width
// is proportional to the section's question count (15+10+10+10+5 / 50).
// The bar fills as the user progresses through that section's questions
// — empty if not yet reached, partial if currently in it, fully filled
// if past it. The currently-active bar is also visually distinguished
// so the user knows which section they're answering.
function SessionHeader({
  questions,
  currentIndex,
  flagged,
  startedAt,
  now,
  onBack,
  onToggleFlag,
}: {
  questions: ExamQuestionEnvelope[];
  currentIndex: number;
  flagged: boolean;
  startedAt: string | undefined;
  now: number;
  onBack: () => void;
  onToggleFlag: () => void;
}) {
  const t = useT();
  const total = questions.length;
  const currentSection = questions[currentIndex]?.section;

  // Countdown — clamped to 0 so we don't show negatives on overrun.
  // Color thresholds: yellow at ≤2 min, red at ≤30 s. Above 2 min the
  // chip stays subdued so it doesn't pull the eye away from the
  // question itself.
  const totalMs = EXAM_DURATION_MINUTES * 60 * 1000;
  const elapsedMs = startedAt ? now - new Date(startedAt).getTime() : 0;
  const remainingMs = Math.max(0, totalMs - elapsedMs);
  const remainingSec = Math.floor(remainingMs / 1000);
  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;
  const timerColor = remainingMs <= 30_000 ? "var(--again)" : remainingMs <= 120_000 ? "var(--hard)" : "var(--text-3)";

  // Per-section progress: how many questions in each section have been
  // *reached* (not necessarily answered — the dot fills as the user
  // advances past, matching the design's "where am I?" intent).
  const reachedBySection: Record<ExamSection, number> = {
    vocab_mcq: 0,
    vocab_type: 0,
    grammar_mcq: 0,
    grammar_fill: 0,
    grammar_transform: 0,
  };
  for (let i = 0; i <= currentIndex && i < total; i++) {
    const s = questions[i]!.section;
    reachedBySection[s]++;
  }

  return (
    <div className="-mx-4 border-b border-border">
      <div className="flex items-center gap-2 px-3 pt-1 pb-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("common.back")}
          className="-ml-2 flex size-9 items-center justify-center rounded-full text-text-2 transition-colors hover:text-text"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold leading-[1.2]">{t("exam.session_title")}</div>
          <div className="mt-0.5 font-mono text-[10px] text-text-3">
            {currentIndex + 1}/{total}
            {currentSection && (
              <>
                {" · "}
                <span className="text-text-2">{t(`exam.section_${currentSection}` as const)}</span>
              </>
            )}
          </div>
        </div>
        {/* Timer chip — subdued in steady state, yellow at ≤2 min,
            red at ≤30 s. `aria-live="polite"` so screen readers
            announce thresholds without spamming on every tick. */}
        {startedAt && (
          <div
            className="flex items-center gap-1 font-mono text-[12px] tabular-nums"
            style={{ color: timerColor }}
            aria-live="polite"
          >
            <Clock className="size-3.5" />
            {mm}:{String(ss).padStart(2, "0")}
          </div>
        )}
        <button
          type="button"
          onClick={onToggleFlag}
          aria-pressed={flagged}
          aria-label={t(flagged ? "exam.unflag" : "exam.flag")}
          className={cn(
            "flex size-9 items-center justify-center rounded-full transition-colors",
            flagged
              ? "text-[color:var(--accent)] hover:bg-[color:var(--accent-soft)]"
              : "text-text-3 hover:bg-[color:var(--surface-2)] hover:text-text",
          )}
        >
          {flagged ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
        </button>
      </div>
      {/* Width-weighted section dots — `flex-grow` proportional to
          section count gives 15:10:10:10:5 widths automatically. The
          inner fill width tracks reached-count / section-count. */}
      <div className="flex h-[3px] w-full gap-[2px] bg-transparent">
        {EXAM_SECTIONS.map((section) => {
          const sectionTotal = EXAM_SECTION_COUNTS[section];
          const reached = reachedBySection[section];
          // sectionTotal is a literal-type union (5 | 10 | 15) — never
          // 0, so no guard needed. Spec uses 5 sections; if it ever
          // shifts, TS will demand this denominator be revisited.
          const fillPct = (reached / sectionTotal) * 100;
          const isActive = section === currentSection;
          return (
            <div
              key={section}
              className="overflow-hidden bg-[color:var(--surface-2)]"
              style={{ flexGrow: sectionTotal }}
            >
              <div
                className="h-full transition-[width] duration-200"
                style={{
                  width: `${fillPct}%`,
                  // Active section is rendered in the accent; sections
                  // already past glow the same colour but at full width
                  // (so they read as "complete"). Future sections stay
                  // empty (transparent fill, surface-2 track shows).
                  background: isActive ? "var(--accent)" : "var(--accent)",
                  opacity: isActive || reached === sectionTotal ? 1 : 0.7,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MCQ widget ──────────────────────────────────────────────────────
function McqWidget({
  options,
  selected,
  onSelect,
}: {
  options: string[];
  selected: string;
  onSelect: (option: string) => void;
}) {
  // Defensive memo: options come pre-shuffled from server. We render in
  // server order — re-shuffling here would change the answer keys.
  const stableOptions = useMemo(() => options, [options]);
  return (
    <div className="flex flex-col gap-2">
      {stableOptions.map((option) => {
        const isSelected = option === selected;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            aria-pressed={isSelected}
            className={cn(
              "rounded-[var(--r-md)] border px-4 py-3 text-left text-[14px] transition-colors",
              isSelected
                ? "border-[color:var(--accent)] bg-[color:var(--accent-soft)] text-[color:var(--accent)] font-semibold"
                : "border-border bg-surface text-text-2 hover:border-border-strong",
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}
